// Generic mock data for UI component demonstrations

// Table data for DataTable demos
export interface TableRow {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'inactive' | 'pending';
  joinedDate: Date;
}

export const mockTableData: TableRow[] = [
  { id: '1', name: 'John Doe', email: 'john@company.com', role: 'Developer', department: 'Engineering', status: 'active', joinedDate: new Date('2022-01-15') },
  { id: '2', name: 'Jane Smith', email: 'jane@company.com', role: 'Manager', department: 'Engineering', status: 'active', joinedDate: new Date('2021-03-10') },
  { id: '3', name: 'Alice Johnson', email: 'alice@company.com', role: 'Designer', department: 'Design', status: 'active', joinedDate: new Date('2022-06-20') },
  { id: '4', name: 'Bob Williams', email: 'bob@company.com', role: 'Developer', department: 'Engineering', status: 'inactive', joinedDate: new Date('2021-09-05') },
  { id: '5', name: 'Charlie Brown', email: 'charlie@company.com', role: 'Developer', department: 'Engineering', status: 'active', joinedDate: new Date('2022-11-12') },
];

// Comment data for CommentThread demos
export interface Comment {
  id: string;
  author: string;
  authorAvatar?: string;
  content: string;
  timestamp: Date;
  likes?: number;
  replies?: Comment[];
}

export const mockComments: Comment[] = [
  {
    id: 'c1',
    author: 'John Doe',
    authorAvatar: '/avatars/1.jpg',
    content: 'This looks great! I think we should move forward with this approach.',
    timestamp: new Date('2024-01-15T10:30:00'),
    likes: 5,
    replies: [
      {
        id: 'c1-r1',
        author: 'Jane Smith',
        authorAvatar: '/avatars/2.jpg',
        content: 'I agree. Let\'s schedule a meeting to discuss implementation details.',
        timestamp: new Date('2024-01-15T11:00:00'),
        likes: 2,
      },
    ],
  },
  {
    id: 'c2',
    author: 'Alice Johnson',
    authorAvatar: '/avatars/3.jpg',
    content: 'I have some concerns about the timeline. Can we extend the deadline by a week?',
    timestamp: new Date('2024-01-15T14:20:00'),
    likes: 3,
  },
];

// Notification data
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

export const mockNotifications: Notification[] = [
  {
    id: 'n1',
    title: 'New task assigned',
    message: 'You have been assigned to "Update user profile page"',
    type: 'info',
    timestamp: new Date('2024-01-15T09:00:00'),
    read: false,
    actionUrl: '/tasks/123',
  },
  {
    id: 'n2',
    title: 'Build completed',
    message: 'Your deployment to production was successful',
    type: 'success',
    timestamp: new Date('2024-01-15T08:30:00'),
    read: false,
  },
  {
    id: 'n3',
    title: 'Meeting reminder',
    message: 'Team standup starts in 15 minutes',
    type: 'warning',
    timestamp: new Date('2024-01-15T08:45:00'),
    read: true,
  },
  {
    id: 'n4',
    title: 'Build failed',
    message: 'The latest build failed. Check the logs for details.',
    type: 'error',
    timestamp: new Date('2024-01-14T18:20:00'),
    read: true,
  },
];

// Timeline/Activity data
export interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  timestamp: Date;
  type: 'default' | 'success' | 'warning' | 'error';
  icon?: string;
}

export const mockTimelineEvents: TimelineEvent[] = [
  {
    id: 'e1',
    title: 'Project created',
    description: 'Website Redesign project was created by Jane Smith',
    timestamp: new Date('2024-01-10T09:00:00'),
    type: 'success',
  },
  {
    id: 'e2',
    title: 'Task assigned',
    description: 'New task assigned to John Doe',
    timestamp: new Date('2024-01-11T10:30:00'),
    type: 'default',
  },
  {
    id: 'e3',
    title: 'Deadline approaching',
    description: 'Project deadline is in 3 days',
    timestamp: new Date('2024-01-12T14:00:00'),
    type: 'warning',
  },
  {
    id: 'e4',
    title: 'Build failed',
    description: 'CI/CD pipeline failed on main branch',
    timestamp: new Date('2024-01-13T16:45:00'),
    type: 'error',
  },
  {
    id: 'e5',
    title: 'Milestone completed',
    description: 'MVP milestone reached - 15/15 tasks completed',
    timestamp: new Date('2024-01-14T11:20:00'),
    type: 'success',
  },
];

// Statistics data for cards/widgets
export interface StatItem {
  id: string;
  label: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease';
  icon?: string;
  color?: string;
}

export const mockStats: StatItem[] = [
  {
    id: 's1',
    label: 'Total Users',
    value: 1234,
    change: 12,
    changeType: 'increase',
    color: 'blue',
  },
  {
    id: 's2',
    label: 'Revenue',
    value: '$45,678',
    change: 8.5,
    changeType: 'increase',
    color: 'green',
  },
  {
    id: 's3',
    label: 'Active Projects',
    value: 23,
    change: 3,
    changeType: 'decrease',
    color: 'orange',
  },
  {
    id: 's4',
    label: 'Tasks Completed',
    value: 156,
    change: 15,
    changeType: 'increase',
    color: 'purple',
  },
];

// Form field options
export const mockCountries = [
  { value: 'us', label: 'United States' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'ca', label: 'Canada' },
  { value: 'au', label: 'Australia' },
  { value: 'de', label: 'Germany' },
  { value: 'fr', label: 'France' },
  { value: 'jp', label: 'Japan' },
  { value: 'cn', label: 'China' },
];

export const mockCategories = [
  { value: 'engineering', label: 'Engineering' },
  { value: 'design', label: 'Design' },
  { value: 'product', label: 'Product' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
];

export const mockPriorities = [
  { value: 'low', label: 'Low', color: 'gray' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'orange' },
  { value: 'urgent', label: 'Urgent', color: 'red' },
];

export const mockStatuses = [
  { value: 'todo', label: 'To Do', color: 'gray' },
  { value: 'in-progress', label: 'In Progress', color: 'blue' },
  { value: 'review', label: 'In Review', color: 'orange' },
  { value: 'done', label: 'Done', color: 'green' },
  { value: 'blocked', label: 'Blocked', color: 'red' },
];

// Breadcrumb data
export const mockBreadcrumbs = [
  { title: 'Home', href: '/' },
  { title: 'Projects', href: '/projects' },
  { title: 'Website Redesign', href: '/projects/123' },
  { title: 'Tasks', href: '/projects/123/tasks' },
];

// Navigation items
export const mockNavItems = [
  { label: 'Dashboard', icon: 'dashboard', href: '/dashboard' },
  { label: 'Projects', icon: 'folder', href: '/projects' },
  { label: 'Tasks', icon: 'checklist', href: '/tasks' },
  { label: 'Team', icon: 'users', href: '/team' },
  { label: 'Settings', icon: 'settings', href: '/settings' },
];

// Tag/Badge data
export const mockTags = [
  'React',
  'TypeScript',
  'UI/UX',
  'Backend',
  'Frontend',
  'Mobile',
  'API',
  'Database',
  'DevOps',
  'Testing',
];

// File/Document data
export interface MockFile {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedBy: string;
  uploadedAt: Date;
  url?: string;
}

export const mockFiles: MockFile[] = [
  {
    id: 'f1',
    name: 'design-mockups.fig',
    type: 'application/figma',
    size: 2456789,
    uploadedBy: 'Alice Johnson',
    uploadedAt: new Date('2024-01-10T10:00:00'),
  },
  {
    id: 'f2',
    name: 'requirements.pdf',
    type: 'application/pdf',
    size: 456789,
    uploadedBy: 'Diana Prince',
    uploadedAt: new Date('2024-01-11T14:30:00'),
  },
  {
    id: 'f3',
    name: 'presentation.pptx',
    type: 'application/vnd.ms-powerpoint',
    size: 3456789,
    uploadedBy: 'Jane Smith',
    uploadedAt: new Date('2024-01-12T09:15:00'),
  },
  {
    id: 'f4',
    name: 'data-export.xlsx',
    type: 'application/vnd.ms-excel',
    size: 123456,
    uploadedBy: 'Oscar Martinez',
    uploadedAt: new Date('2024-01-13T16:00:00'),
  },
];

// Chart data
export const mockChartData = {
  lineChart: [
    { month: 'Jan', value: 400 },
    { month: 'Feb', value: 300 },
    { month: 'Mar', value: 600 },
    { month: 'Apr', value: 800 },
    { month: 'May', value: 500 },
    { month: 'Jun', value: 700 },
  ],
  barChart: [
    { category: 'Design', value: 45 },
    { category: 'Development', value: 78 },
    { category: 'Testing', value: 32 },
    { category: 'Deployment', value: 15 },
  ],
  pieChart: [
    { name: 'Completed', value: 120 },
    { name: 'In Progress', value: 45 },
    { name: 'To Do', value: 30 },
  ],
};

// Progress data
export interface ProgressItem {
  id: string;
  label: string;
  current: number;
  total: number;
  percentage: number;
  color?: string;
}

export const mockProgress: ProgressItem[] = [
  { id: 'p1', label: 'Design Phase', current: 8, total: 10, percentage: 80, color: 'blue' },
  { id: 'p2', label: 'Development', current: 45, total: 60, percentage: 75, color: 'green' },
  { id: 'p3', label: 'Testing', current: 12, total: 20, percentage: 60, color: 'orange' },
  { id: 'p4', label: 'Documentation', current: 3, total: 15, percentage: 20, color: 'purple' },
];
